// Copyright © 2026 ApexRuntime Labs by Shashi (https://github.com/spshah). All rights reserved.

import { useState, useEffect, useRef } from 'react';
import { TrendingDown, TrendingUp, Tag } from 'lucide-react';
import api from '../../api/axios';
import { formatINR } from '../../utils/currency';

export default function SummaryHeader() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const fetchingRef = useRef(false);

  useEffect(() => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    setLoading(true);

    api
      .get('/api/budget/reports/totals/')
      .then((res) => setSummary(res.data))
      .catch(() => setError('Failed to load summary.'))
      .finally(() => {
        setLoading(false);
        fetchingRef.current = false;
      });
  }, []);

  if (loading)
    return (
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 animate-pulse">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-16 bg-gray-100 rounded-xl" />
        ))}
      </div>
    );

  // if (error) return <p className="text-sm text-red-500">{error}</p>;

  if (!summary) return null;

  const cards = [
    {
      label: 'Expenses This Month',
      value: formatINR(summary.total_expense ?? 0),
      icon: <TrendingDown size={16} className="text-red-400" />,
    },
    {
      label: 'Expenses This Year',
      value: formatINR(summary.yearly_expense ?? 0),
      icon: <TrendingUp size={16} className="text-amber-400" />,
    },
    {
      label: 'Top Categories',
      value: summary.top_categories?.slice(0, 3).join(', ') || '—',
      icon: <Tag size={16} className="text-indigo-400" />,
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      {error && <p className="text-sm text-red-500 col-span-full">{error}</p>}
      {cards.map(({ label, value, icon }) => (
        <div
          key={label}
          className="bg-white rounded-xl border border-gray-200 p-4 flex items-start gap-3"
        >
          <div className="mt-0.5">{icon}</div>
          <div>
            <p className="text-xs text-gray-500">{label}</p>
            <p className="text-sm font-semibold text-gray-800 mt-0.5 truncate">
              {value}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}
